import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams, Navigate } from 'react-router-dom';
import { SparkIcon, Tabs, TooltipTrigger, sparkToast } from '@spark';
import { GlobalContext } from '@/app/providers/globalState';
import DataOverviewCard from '@/features/datasets/components/DataCollection/dataOverview/DataOverviewCard';

import ModelSetup from '@/pages/ModelSetup';
import { DataSelection } from '@/pages/DataSelection';
import { RetrainTaskModal } from '@/features/trainings/components/RetrainTaskModal';
import { Evaluation } from '@/pages/Evaluation';
import { Playground } from '@/pages/Playground';
import AddDataModal from '@/features/datasets/components/AddDataModal';
import GenerateDataWithAIModal from '@/features/datasets/components/DataCollection/GenerateDataWithAI/GenerateDataWithAIModal';
import RetrievalAgentModal from '@/features/datasets/components/DataCollection/RetrievalAgent/RetrievalAgentModal';
import { DuplicateTaskModal } from '@/features/tasks/components/HomePage/DuplicateTaskModal';
import { EditTaskModal } from '@/features/tasks/components/HomePage/EditTaskModal';
import { MoveTasksModal } from '@/features/tasks/components/HomePage/MoveTasksModal';
import ProjectCreatedBar from '@/features/tasks/components/HomePage/ProjectCreatedBar';
import TaskPageHeader from '@/features/tasks/components/Task/TaskPageHeader';
import TaskModelDetailsTab from '@/features/tasks/components/Task/TaskModelDetailsTab';
import TaskInferenceTab from '@/features/tasks/components/Task/TaskInferenceTab';
import ModelProcessingModal from '@/features/tasks/components/Task/ModelProcessingModal';
import {
    TASK_MANAGEMENT_PAGE_ORDER,
    TASK_MANAGEMENT_WALKTHROUGH_ID,
    WALKTHROUGH_AUTO_START,
    WalkthroughRunner,
    useTaskManagementTour,
} from '@/features/walkthrough';
import { hasEvaluationResults } from '@/features/tasks/utils/deriveInitialTaskAction';
import { useEvaluationPresence } from '@/features/tasks/hooks/useEvaluationPresence';
import QueryFileModal from '@/features/tasks/components/Task/QueryFileModal';
import { QUERY_FILE_LANGUAGES } from '@/features/tasks/components/Task/DeployDropdown';
import { MOVE_LOCKED_TOOLTIP, undeployFirstTooltip } from '@/shared/utils/deploymentUx';
import RecalculateEvaluationParamsModal from '@/features/evaluation/components/RecalculateEvaluationParamsModal';
import VersionsDiffModal from '@/features/evaluation/components/versionsDiff/VersionsDiffModal';
import { CURRENT_VERSION_EVENT_ID } from '@/features/evaluation/api/versionsDiffApi';
import {
    useTaskActions,
    openConfirmMenuDialog,
} from '@/features/tasks/components/HomePage/task/useTaskActions';
import { useTaskPageActions } from '@/features/tasks/hooks/useTaskPageActions';
import { useTaskChangeStatus } from '@/features/tasks/hooks/useTaskChangeStatus';
import {
    TASK_PAGE_TABS,
    useTaskActionUrlState,
} from '@/features/tasks/hooks/useTaskActionUrlState';
import { taskUrlForUsecase } from '@/features/tasks/utils/navigation';
import { getMoveDestinations } from '@/features/tasks/utils/bulkMoveTasks';
import { runAIGenerationDataset } from '@/features/datasets/utils/aiGenerationDataset';
import { runRetrievalAgent } from '@/features/datasets/utils/retrievalAgentDataset';
import { gateDroppedFilesForUploadModal } from '@/features/datasets/utils/droppedFilesColumnOverlap';
import {
    deploymentTagProps,
    getDeploymentBadge,
} from '@/features/tasks/components/HomePage/task/taskSummary';
import {
    deploy_in_progress,
    distillation_in_progress,
    evaluation_in_progress,
    undeploy_in_progress,
} from '@/shared/constants/taskStatuses';
import { TaskType, isGenerativeTask } from '@/shared/domain/taskTypes';
import { getTrainingTaskDisplayName } from '@/features/trainings/components/trainingsUtils';
import {
    ConfigureDeploymentFlowModals,
    fetchTrainingsForConfigureModal,
    useConfigureDeploymentFlow,
} from '@/features/deployments';
import { DEFAULT_CLIENT_ID } from '@/config/client';
import { useTrainingProgress } from '@/features/trainings/hooks/useTrainingProgress';
import TrainingProgressBanner from '@/features/trainings/components/CurrentTraining/TrainingProgressBanner';
import LabelGenerationContinueBanner from '@/features/trainings/components/CurrentTraining/LabelGenerationContinueBanner';
import AutoTrainProgressBanner from '@/features/trainings/components/CurrentTraining/AutoTrainProgressBanner';
import { taskActiveBanner } from '@/features/trainings/utils/taskBannerPriority';
import { consumeAutoTrainNotifyModalShown } from '@/features/trainings/utils/autoTrain';
import { useAuth } from '@/features/auth/AuthContext';
import { data_from_server_post } from '@/shared/http/client';
import '@/features/tasks/styles/task-page.css';
import '@/features/tasks/styles/task-drawer.css';

/** Legacy drawer screen keys — now mapped 1:1 onto page tabs. */
const TAB_SCREEN_KEYS = new Set([
    'model-setup',
    'data-selection',
    'evaluation-from-session',
]);

/**
 * Task page — full-page tabbed layout (Figma file KcQvkthzkvYFRqHHj0hCo4):
 * breadcrumb + title header with the task actions, then five tabs (Data
 * Overview / Model Configuration / Data Editor / Evaluations / Versions)
 * hosting the step screens that previously opened in right-side drawers.
 * The Playground keeps its drawer, opened from the header's game icon
 * button.
 *
 * Route: `/task/:taskName?task_id=...&action=<tab>`
 */
const TaskPage = ({ moveToScreen, onCreateProject, onAddTask }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { groupName: rawGroupName, taskName: rawTaskName } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { role, user, isSaas } = useAuth();
    const {
        allApps,
        allUsecases,
        setCurrentUsecaseId,
        refreshUsecases,
        isFetchingUsecases,
        setProgressStatus,
        seedGenerationProgress,
        seedRetrievalAgentProgress,
        updateStatus,
        updateUsecase,
        markTrainingStatusCanceled,
        evaluationResultsReady,
        realtimeResyncTick,
    } = useContext(GlobalContext);

    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [uploadSource, setUploadSource] = useState('upload');
    const [uploadTarget, setUploadTarget] = useState(undefined);
    /** Files dropped on Data Overview upload cards — fed into AddDataModal. */
    const [uploadDroppedFiles, setUploadDroppedFiles] = useState(null);
    const addDataRequestRef = useRef(0);
    const [isGenerateWithAiOpen, setIsGenerateWithAiOpen] = useState(false);
    const [isRetrievalAgentOpen, setIsRetrievalAgentOpen] = useState(false);
    const [dataOverviewRefreshKey, setDataOverviewRefreshKey] = useState(0);
    const [isProcessingModalOpen, setIsProcessingModalOpen] = useState(false);
    const [isRetrainModalOpen, setIsRetrainModalOpen] = useState(false);
    const [isReevaluateModalOpen, setIsReevaluateModalOpen] = useState(false);
    const [isVersionsDiffOpen, setIsVersionsDiffOpen] = useState(false);
    const [isReevaluating, setIsReevaluating] = useState(false);
    const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
    const [moveLoading, setMoveLoading] = useState(false);
    const modelSetupRef = useRef(null);

    // Training just kicked off from the creation wizard — show the "Your
    // Model will be processed now" modal (Figma 3001-68567) centered over the
    // Data Overview tab. Clear the router state so back/refresh won't re-open.
    useEffect(() => {
        if (!location.state?.showProcessingModal) return;
        setIsProcessingModalOpen(true);
        navigate(
            { pathname: location.pathname, search: location.search },
            { replace: true, state: null }
        );
    }, [location.pathname, location.search, location.state, navigate]);

    const closeProcessingModal = useCallback(() => setIsProcessingModalOpen(false), []);

    const refreshDataOverview = useCallback(() => {
        setDataOverviewRefreshKey((key) => key + 1);
    }, []);

    const taskName = rawTaskName ? decodeURIComponent(rawTaskName) : '';
    const taskIdFromUrl = searchParams.get('task_id') || searchParams.get('objective') || null;

    const subGroup = useMemo(() => {
        if (!allApps.length) return null;

        if (taskIdFromUrl) {
            for (const app of allApps) {
                const found = (app.sub_groups || []).find((sg) =>
                    (sg.usecases || []).some((uc) => uc._id === taskIdFromUrl)
                );
                if (found) return found;
            }
        }

        // Backward compatible fallback for older /task/:groupName/:taskName links.
        const groupName = rawGroupName ? decodeURIComponent(rawGroupName) : '';
        if (groupName && taskName) {
            const app = allApps.find((a) => a.group_name === groupName);
            if (app) {
                const found = (app.sub_groups || []).find((sg) => sg.sub_group_name === taskName);
                if (found) return found;
            }
        }

        // New URL shape: /task/:taskName?task_id=...
        if (taskName) {
            for (const app of allApps) {
                const found = (app.sub_groups || []).find((sg) => sg.sub_group_name === taskName);
                if (found) return found;
            }
        }

        return null;
    }, [allApps, rawGroupName, taskIdFromUrl, taskName]);

    const usecases = subGroup?.usecases || [];

    const activeUsecaseId = useMemo(() => {
        const urlTaskId = searchParams.get('task_id') || searchParams.get('objective');
        if (urlTaskId && usecases.some((u) => u._id === urlTaskId)) {
            return urlTaskId;
        }
        return usecases[0]?._id || null;
    }, [searchParams, usecases]);

    const activeUsecase = useMemo(
        () => usecases.find((u) => u._id === activeUsecaseId) || null,
        [usecases, activeUsecaseId]
    );

    const effectiveUsecase = activeUsecase || usecases[0] || null;

    // Task-management tour: only meaningful once the task has evaluation
    // results (its marks sit on the Evaluations tab). Gates the header's
    // "View Guided Tour" button and the first-visit auto-start. Confirmed by
    // the presence probe, not by status, so in-progress/failed runs don't count.
    const tourProbeUsecases = useMemo(
        () => (effectiveUsecase ? [effectiveUsecase] : []),
        [effectiveUsecase]
    );
    const evalPresenceByUsecaseId = useEvaluationPresence({
        usecases: tourProbeUsecases,
        evaluationResultsReady,
        realtimeResyncTick,
    });
    const taskTourAvailable = hasEvaluationResults(effectiveUsecase, evalPresenceByUsecaseId);

    // "Notify Me" on the processing modal — register the logged-in user's
    // email for a training-completion email. Only offered when we actually
    // have an email (auth disabled sessions have an empty one).
    const canNotifyByEmail = Boolean(user?.email && effectiveUsecase?._id);
    const handleNotifyMe = useCallback(() => {
        closeProcessingModal();
        data_from_server_post(
            'notify_on_training_completion',
            {
                client_id: effectiveUsecase?.client_id,
                usecase_id: effectiveUsecase?._id,
            },
            (result) => {
                if (result?.success) {
                    sparkToast.success("We'll email you when your model is ready");
                } else if (result?.message) {
                    sparkToast.error(result.message);
                }
            },
            () => sparkToast.error('Failed to register the notification')
        );
    }, [closeProcessingModal, effectiveUsecase?._id, effectiveUsecase?.client_id]);

    useEffect(() => {
        if (activeUsecaseId) {
            setCurrentUsecaseId(activeUsecaseId);
        }
    }, [activeUsecaseId, setCurrentUsecaseId]);

    const {
        activeTabKey,
        isPlaygroundOpen,
        updateTaskSearchParams,
        openTab,
        openPlayground,
        closePlayground,
    } = useTaskActionUrlState({
        searchParams,
        setSearchParams,
        usecases,
        activeUsecaseId,
        setCurrentUsecaseId,
    });

    // Task-management tour: its marks live on the Evaluations tab, so every
    // start (first visit, resume, "View Guided Tour") lands there first. The
    // runner below only mounts once the task has evaluation results.
    const openEvaluationTab = useCallback(
        () => openTab('evaluation-from-session', activeUsecaseId),
        [openTab, activeUsecaseId]
    );
    const { currentPage: taskTourPage } = useTaskManagementTour({
        onStart: openEvaluationTab,
        isEvaluationTabActive: activeTabKey === 'evaluation-from-session',
        isPlaygroundOpen,
    });

    // Training kicked off from Retrain (or the creation wizard finishing
    // onto this page) flips the usecase status to "Distillation in Progress"
    // as soon as the distillation event is created. Catch that transition
    // and show the processing modal.
    const prevTrainSignalRef = useRef({
        id: effectiveUsecase?._id || null,
        status: effectiveUsecase?.status,
    });
    useEffect(() => {
        const id = effectiveUsecase?._id || null;
        const status = effectiveUsecase?.status;
        const prev = prevTrainSignalRef.current;
        prevTrainSignalRef.current = { id, status };
        if (
            prev.id === id &&
            status === distillation_in_progress &&
            prev.status !== distillation_in_progress
        ) {
            // Auto-train armed in this tab: the user already saw the
            // "Training will start when data is ready" modal at arm time —
            // don't show its "Training Has Started!" duplicate now.
            if (consumeAutoTrainNotifyModalShown(id)) return;
            setIsProcessingModalOpen(true);
        }
    }, [effectiveUsecase?._id, effectiveUsecase?.status]);

    // Cross-tab training banner (Figma 3561:16630) — training state lives at
    // the page level so the banner stays visible on every tab.
    const {
        showTrainingProgressCard,
        currentDistillationEventIdOfActiveTraining,
        currentTrainingStatus,
        currentTrainingProgress,
        currentTrainingErrorDetails,
        currentTrainingUsecaseId,
        currentTrainingVersion,
        closeProgressCard,
        clearActiveTraining,
    } = useTrainingProgress(effectiveUsecase);

    // Banner "X" → confirm → stop (delete) the running training. Same flow
    // the old in-tab progress card offered through its Popconfirm.
    const handleConfirmStopTraining = useCallback(() => {
        if (!currentDistillationEventIdOfActiveTraining) {
            closeProgressCard();
            return;
        }
        data_from_server_post(
            'delete_training',
            {
                client_id: effectiveUsecase?.client_id,
                distillation_event_id: currentDistillationEventIdOfActiveTraining,
            },
            (result) => {
                if (result?.success) {
                    markTrainingStatusCanceled(
                        currentDistillationEventIdOfActiveTraining,
                        effectiveUsecase?._id
                    );
                    if (result?.usecase_status) {
                        updateStatus(effectiveUsecase?._id, result.usecase_status);
                    }
                    refreshUsecases();
                    sparkToast.success('Training was deleted successfully');
                } else {
                    sparkToast.error(result?.message || 'Failed to delete training');
                }
                clearActiveTraining();
                closeProgressCard();
            },
            () => {
                sparkToast.error('Server error while deleting training');
                clearActiveTraining();
                closeProgressCard();
            }
        );
    }, [
        currentDistillationEventIdOfActiveTraining,
        effectiveUsecase?._id,
        effectiveUsecase?.client_id,
        markTrainingStatusCanceled,
        updateStatus,
        refreshUsecases,
        clearActiveTraining,
        closeProgressCard,
    ]);

    const activeBannerKind = taskActiveBanner({
        showTrainingProgressCard,
        usecase: effectiveUsecase,
    });
    const trainingBanner = activeBannerKind === 'training' ? (
        <TrainingProgressBanner
            distillationEventId={currentDistillationEventIdOfActiveTraining}
            progress={currentTrainingProgress}
            status={currentTrainingStatus}
            errorDetails={currentTrainingErrorDetails}
            usecaseId={currentTrainingUsecaseId}
            version={currentTrainingVersion}
            onConfirmStop={handleConfirmStopTraining}
        />
    ) : activeBannerKind === 'label-generation' ? (
        <LabelGenerationContinueBanner
            usecaseId={effectiveUsecase._id}
            clientId={effectiveUsecase.client_id}
            pending={effectiveUsecase.improve_retrain_pending}
            onPendingChange={(next) => updateUsecase(effectiveUsecase._id, {
                improve_retrain_pending: next,
            })}
        />
    ) : activeBannerKind === 'auto-train' ? (
        <AutoTrainProgressBanner usecase={effectiveUsecase} />
    ) : null;

    // ----- Task actions (edit / duplicate / delete / deploy) -----

    const {
        handleDeleteUsecase,
        handleBulkDeleteUsecases,
        handleBulkMoveUsecases,
        handleModelDeployment,
        handleDuplicateUsecase,
        handleEditTask,
        isDuplicateTaskModalOpen,
        taskToDuplicate,
        closeDuplicateTaskModal,
        handleCreateDuplicateTask,
        projectCreatedBar,
        closeProjectCreatedBar,
        isEditTaskModalOpen,
        taskToEdit,
        editTaskMode,
        closeEditTaskModal,
        handleUpdateTask,
    } = useTaskPageActions();

    const handleStartDuplicatedTask = useCallback(() => {
        if (!projectCreatedBar.usecaseId) {
            closeProjectCreatedBar();
            return;
        }
        const usecase = allUsecases.find((u) => u._id === projectCreatedBar.usecaseId);
        const url = usecase
            ? taskUrlForUsecase(usecase, { objective: usecase._id })
            : null;
        if (!url) {
            closeProjectCreatedBar();
            return;
        }
        setCurrentUsecaseId(usecase._id);
        closeProjectCreatedBar();
        navigate(url);
    }, [
        allUsecases,
        closeProjectCreatedBar,
        navigate,
        projectCreatedBar.usecaseId,
        setCurrentUsecaseId,
    ]);

    const {
        onMenuOpenChange,
        handleDuplicateTask,
        handleDeleteTask,
        deployMenuItem,
        undeployMenuItem,
        fetchAvailableVersions,
        availableVersions,
        isMoveLocked,
    } = useTaskActions({
        subGroup,
        onDeleteUsecase: handleDeleteUsecase,
        onBulkDeleteUsecases: handleBulkDeleteUsecases,
        onModelDeployment: handleModelDeployment,
        onDuplicateUsecase: handleDuplicateUsecase,
        onEditTask: handleEditTask,
    });

    const firstUsecase = usecases[0] || null;
    const isViewer = role === 'Viewer';

    const taskTitle =
        (subGroup?.sub_group_name || effectiveUsecase?.sub_group_name || '').trim() ||
        effectiveUsecase?.usecase_name ||
        '';

    // Deploy (and change tracking, below) is only offered once at least one
    // version finished training. `usecase.version` is bumped when a training
    // *starts*, so the reliable signal is the deployable-versions API
    // (completed trainings only) — prefetch it here instead of waiting for
    // the deploy menu to open. Deployed usecases are included: the redesigned
    // Deploy dropdown lists every completed version even while one of them is
    // deployed. Generative tasks never deploy, but their completed versions
    // gate the Retrain / Revaluate primary button the same way.
    useEffect(() => {
        usecases.forEach((uc) => {
            if (uc) {
                fetchAvailableVersions(uc);
            }
        });
    }, [usecases, fetchAvailableVersions]);

    const hasCompletedVersion = usecases.some(
        (uc) => (availableVersions[uc?._id] || []).length > 0
    );
    const showDeploy =
        Boolean(deployMenuItem) && !deployMenuItem?.disabled && hasCompletedVersion;
    const deployMenuItems = deployMenuItem?.children || [];

    const deployedUsecases = usecases.filter((uc) => uc?.is_deployed);
    const deployedVersions = [
        ...new Set(
            deployedUsecases
                .map((uc) => uc?.deployed_version)
                .filter((v) => v !== null && v !== undefined && v !== '')
        ),
    ];
    const undeployLabel = undeployMenuItem
        ? deployedVersions.length === 1
            ? `Undeploy V${Number(deployedVersions[0])}`
            : 'Undeploy'
        : null;
    // Multi-objective tasks with several deployed subtasks pick which one to
    // undeploy from the submenu built by useTaskActions.
    const undeployMenuItems =
        deployedUsecases.length > 1 ? undeployMenuItem?.children || [] : [];
    const handleUndeploy = useCallback(() => {
        undeployMenuItem?.onClick?.();
    }, [undeployMenuItem]);

    // ----- Redesigned Deploy dropdown (Figma 3480:19247) -----
    // Single-objective, non-generative tasks get the Versions + Query File
    // dropdown; undeploy moves inline onto the deployed version row, so the
    // standalone "Undeploy V{n}" button is not rendered for them.
    // Multi-objective tasks keep the legacy antd submenu path above.
    const [queryFileModal, setQueryFileModal] = useState({
        open: false,
        language: 'python',
    });

    const singleDeployUsecase =
        usecases.length === 1 && firstUsecase && !isGenerativeTask(firstUsecase.task_type)
            ? firstUsecase
            : null;

    // The version whose deploy was just requested — `deployed_version` only
    // reaches the usecase once the deploy completes, so the in-flight target
    // is remembered locally from the click. Shown by the title pill
    // ("Deploy in Progress (V3)") and next to that version's row in the
    // kebab Deploy submenu; cleared when the "updateStatus" WebSocket event
    // resolves the request.
    const [pendingDeployVersion, setPendingDeployVersion] = useState(null);
    const deployRequestInFlight = singleDeployUsecase?.status === deploy_in_progress;
    useEffect(() => {
        if (!deployRequestInFlight) setPendingDeployVersion(null);
    }, [deployRequestInFlight]);

    const deployDropdown = useMemo(() => {
        if (!singleDeployUsecase || isViewer) return null;
        const versions = availableVersions[singleDeployUsecase._id];
        const isDeployed = Boolean(singleDeployUsecase.is_deployed);
        // Same gate as the legacy button: nothing to show until at least one
        // version completed training (a deployed task always has one).
        if (!isDeployed && (versions || []).length === 0) return null;
        return {
            versions: versions || [],
            loading: versions === undefined,
            deployedVersion: isDeployed ? singleDeployUsecase.deployed_version : null,
            // Set optimistically by handleModelDeployment, then resolved by
            // the "updateStatus" WebSocket event (→ Deployed / Ready to
            // Deploy) — the trigger and the affected version row spin
            // until then.
            inProgressAction:
                singleDeployUsecase.status === deploy_in_progress
                    ? 'deploy'
                    : singleDeployUsecase.status === undeploy_in_progress
                        ? 'undeploy'
                        : null,
            onDeployVersion: (version) => {
                setPendingDeployVersion(version);
                handleModelDeployment(singleDeployUsecase._id, 'deploy', version);
            },
            onUndeploy: () => handleModelDeployment(singleDeployUsecase._id, 'undeploy'),
            onOpenQueryFile: (language) => setQueryFileModal({ open: true, language }),
        };
    }, [singleDeployUsecase, isViewer, availableVersions, handleModelDeployment]);

    // Tag next to the task title (same badge logic as the homepage
    // TaskCard): "Deployed (V1)" success tag once a deploy completes, or the
    // "Deploy in Progress" / "Undeploy in Progress" processing tag while the
    // request is in flight — rendered by the header as an outlined pill with
    // a spinning loader icon. When the deploy target is known (kicked off
    // from this page), the pill also names it: "Deploy in Progress (V3)".
    const deploymentTag = useMemo(() => {
        const tag = deploymentTagProps(getDeploymentBadge(usecases));
        if (
            tag?.variant === 'processing' &&
            tag.label === deploy_in_progress &&
            pendingDeployVersion != null
        ) {
            return { ...tag, label: `${tag.label} (V${Number(pendingDeployVersion)})` };
        }
        return tag;
    }, [usecases, pendingDeployVersion]);

    const queryFileProjectName =
        singleDeployUsecase?.group_name ||
        allUsecases.find((u) => u._id === singleDeployUsecase?._id)?.group_name ||
        '';

    const loadTrainingsForConfigure = useCallback(
        () =>
            fetchTrainingsForConfigureModal({
                allUsecases,
                clientId: activeUsecase?.client_id || DEFAULT_CLIENT_ID,
            }),
        [allUsecases, activeUsecase?.client_id]
    );
    const handleDownloadDeploySuccess = useCallback(
        (training) => {
            const usecaseId = training?.llm_usecase_id || activeUsecaseId;
            if (usecaseId) updateStatus(usecaseId, deploy_in_progress);
        },
        [updateStatus, activeUsecaseId]
    );
    const configureFlow = useConfigureDeploymentFlow({
        setProgressStatus,
        onDeploySuccess: handleDownloadDeploySuccess,
        loadTrainings: loadTrainingsForConfigure,
    });

    // Download is always offered once the task has a model to download:
    // either the usecase carries a version (bumped when a training starts) or
    // the deployable-versions API reports at least one successfully completed
    // version. When `version` is missing, fall back to the latest completed
    // version so the download modal still resolves the right training row.
    const activeCompletedVersions = availableVersions[activeUsecase?._id] || [];
    const downloadDisabledReason =
        activeUsecase?.version == null && activeCompletedVersions.length === 0
            ? 'No trained model to download yet'
            : null;
    const handleDownloadModel = useCallback(() => {
        if (!activeUsecase) return;
        const completedVersions = availableVersions[activeUsecase._id] || [];
        const version =
            activeUsecase.version ??
            (completedVersions.length
                ? Math.max(...completedVersions.map(Number))
                : null);
        if (version == null) return;
        configureFlow.handleDownloadTraining({
            ...activeUsecase,
            version,
            llm_usecase_id: activeUsecase._id,
            usecase_name: getTrainingTaskDisplayName({
                ...activeUsecase,
                sub_group_name: subGroup?.sub_group_name || activeUsecase.sub_group_name,
            }),
        });
    }, [
        activeUsecase,
        availableVersions,
        subGroup?.sub_group_name,
        configureFlow.handleDownloadTraining,
    ]);

    const moveDestinations = useMemo(
        () => getMoveDestinations(allApps, [firstUsecase?.group_name].filter(Boolean)),
        [allApps, firstUsecase?.group_name]
    );

    const handleMoveTask = useCallback(async (destName) => {
        setMoveLoading(true);
        try {
            const result = await handleBulkMoveUsecases(usecases, destName);
            if (result?.success !== false) {
                setIsMoveModalOpen(false);
            }
            return result;
        } finally {
            setMoveLoading(false);
        }
    }, [handleBulkMoveUsecases, usecases]);

    const flushModelSetupSaves = useCallback(async () => {
        try {
            await modelSetupRef.current?.flushPendingSaves?.();
        } catch {
            // Autosave errors stay on the Model Configuration surface.
        }
    }, []);

    // ----- Primary header action (Deploy / Retrain / Revaluate) -----
    // Content-based change detection since the latest training /
    // re-evaluation run: dataset changes need a retrain; benchmark-only or
    // evaluation-config changes need a re-evaluation; otherwise Deploy stays
    // primary. Applies to single-objective tasks with ≥1 completed version:
    // non-generative ones via the redesigned Deploy dropdown, generative ones
    // (Summarization / Content Generation — no Deploy at all) with the same
    // change logic, except "no changes" renders no primary button instead of
    // Deploy. Tasks that never trained keep the current behavior.
    const singleUsecase = usecases.length === 1 ? firstUsecase : null;
    const generativeChangeTracking = Boolean(
        singleUsecase &&
            isGenerativeTask(singleUsecase.task_type) &&
            !isViewer &&
            (availableVersions[singleUsecase._id] || []).length > 0
    );
    const changeStatus = useTaskChangeStatus({
        usecase: singleUsecase,
        enabled: Boolean(deployDropdown) || generativeChangeTracking,
        refreshKey: dataOverviewRefreshKey,
    });
    const { refresh: refreshChangeStatus } = changeStatus;

    // First-train promotion: a managed task that has data but no completed
    // version yet — e.g. a freshly duplicated ready-to-deploy task — gets a
    // primary "Train" button (same train flow as Retrain, first-run label).
    // Hidden while the first training / evaluation run is in flight, and
    // scoped to single-objective tasks like the Retrain / Revaluate
    // promotion. The creation wizard renders its own page, so this only
    // applies when managing an existing task.
    const showTrainAction = Boolean(
        singleUsecase &&
            !isViewer &&
            (singleUsecase.datasets || []).length > 0 &&
            !singleUsecase.is_deployed &&
            (availableVersions[singleUsecase._id] || []).length === 0 &&
            ![distillation_in_progress, evaluation_in_progress].includes(
                singleUsecase.status
            )
    );

    const mainAction = changeStatus.datasetsChanged
        ? 'retrain'
        : changeStatus.benchmarksChanged || changeStatus.configChanged
            ? 'reevaluate'
            : showTrainAction
                ? 'train'
                : 'deploy';

    const handleRetrainClick = useCallback(() => {
        void flushModelSetupSaves().then(() => setIsRetrainModalOpen(true));
    }, [flushModelSetupSaves]);

    // Revaluate main button reuses the Evaluations-tab flow: the confirm
    // modal, then `recalculate-evaluation` on the latest major version.
    const handleReevaluateClick = useCallback(() => {
        void flushModelSetupSaves().then(() => setIsReevaluateModalOpen(true));
    }, [flushModelSetupSaves]);

    const handleConfirmReevaluate = useCallback(async ({ evaluationScope = 'full', benchmarkNames } = {}) => {
        if (!singleUsecase || singleUsecase.version == null) return;
        setIsReevaluating(true);
        try {
            const body = {
                client_id: singleUsecase.client_id,
                llm_usecase_id: singleUsecase._id,
                version: Math.floor(Number(singleUsecase.version)),
                evaluation_scope: evaluationScope,
            };
            if (Array.isArray(benchmarkNames)) {
                body.benchmark_names = benchmarkNames;
            }
            const response = await data_from_server_post('recalculate-evaluation', body);
            if (response?.success) {
                setIsReevaluateModalOpen(false);
                sparkToast.success('Re evaluation triggered successfully');
                // The new recalc event resets the benchmark/config snapshots,
                // so the primary button falls back to Deploy (or to no button
                // on generative tasks). The in-page banner shows progress.
                refreshChangeStatus();
            } else {
                sparkToast.error(response?.message || 'Failed to trigger the re-evaluation');
            }
        } catch {
            sparkToast.error('Server error while triggering the re-evaluation');
        } finally {
            setIsReevaluating(false);
        }
    }, [singleUsecase, refreshChangeStatus]);

    // Deploy inside the kebab (as a side flyout submenu mirroring the
    // DeployDropdown panel: "Versions" / "Query File" section headers, an
    // inline Undeploy tag on the deployed version row, eye icons on the
    // query-file rows). Always offered once the task has at least one
    // completed version — even when Deploy is already the primary header
    // button — so the kebab menu stays consistent regardless of which action
    // is promoted.
    const deployKebabItem = useMemo(() => {
        if (!deployDropdown) return null;
        const {
            versions,
            deployedVersion,
            inProgressAction,
            onDeployVersion,
            onUndeploy,
            onOpenQueryFile,
        } = deployDropdown;
        const hasDeployed = deployedVersion !== null && deployedVersion !== undefined;
        const deployedLabel = hasDeployed ? `V${Number(deployedVersion)}` : null;
        const inProgress = Boolean(inProgressAction);
        const versionChildren = (versions || []).map((version) => {
            const versionNumber = Number(version);
            // The row whose deploy is in flight — inline "Deploy in Progress"
            // pill (same style as the title pill) where the Undeploy tag will
            // appear once the deploy completes.
            const isPendingDeployRow =
                inProgressAction === 'deploy' &&
                pendingDeployVersion != null &&
                versionNumber === Number(pendingDeployVersion);
            if (isPendingDeployRow) {
                return {
                    key: `deploy-version-${version}`,
                    label: <span className="fs-unmask">{`V${versionNumber}`}</span>,
                    disabled: true,
                    suffix: (
                        <span className="task-page-header__deployment-progress-tag spark-dropdown-item-suffix fs-unmask">
                            <SparkIcon
                                slug="loader"
                                size="small"
                                className="task-page-header__deployment-progress-icon"
                            />
                            Deploy in Progress
                        </span>
                    ),
                };
            }
            const isDeployedRow = hasDeployed && versionNumber === Number(deployedVersion);
            if (isDeployedRow) {
                // The Undeploy tag is the visual affordance (same as the
                // DeployDropdown panel); the whole row triggers the undeploy,
                // like the previous kebab "Undeploy V{n}" row.
                return {
                    key: `deploy-version-${version}`,
                    label: <span className="fs-unmask">{`V${versionNumber}`}</span>,
                    disabled: inProgress,
                    disableWhenReadOnly: true,
                    suffix: (
                        <TooltipTrigger
                            text={
                                `V${versionNumber} is currently deployed. Click to undeploy ` +
                                'it and stop serving this model version.'
                            }
                            placement="left"
                            className="spark-dropdown-item-suffix"
                        >
                            <span className="deploy-dropdown__undeploy-tag fs-unmask">
                                <SparkIcon slug="arrow-back-up" size="small" />
                                Undeploy
                            </span>
                        </TooltipTrigger>
                    ),
                    onClick: () => onUndeploy?.(),
                };
            }
            // Only one version can be deployed at a time; also blocked
            // while a deploy/undeploy request is in flight.
            const disabled = hasDeployed || inProgress;
            const disabledTooltip = inProgress
                ? 'A deployment change is in progress. Please wait for it to finish.'
                : undeployFirstTooltip(deployedLabel);
            return {
                key: `deploy-version-${version}`,
                label: disabled ? (
                    <TooltipTrigger text={disabledTooltip} placement="left">
                        <span className="fs-unmask">{`V${versionNumber}`}</span>
                    </TooltipTrigger>
                ) : (
                    <span className="fs-unmask">{`V${versionNumber}`}</span>
                ),
                disabled,
                disableWhenReadOnly: true,
                onClick: () => onDeployVersion?.(version),
            };
        });
        const queryFileChildren = QUERY_FILE_LANGUAGES.map(({ id, label }) => ({
            key: `deploy-query-file-${id}`,
            label: <span className="fs-unmask">{label}</span>,
            suffix: (
                <SparkIcon slug="eye" size="small" className="spark-dropdown-item-suffix" />
            ),
            onClick: () => onOpenQueryFile?.(id),
        }));
        return {
            key: 'deploy-task',
            label: <span className="fs-unmask">Deploy</span>,
            icon: <SparkIcon slug="rocket" size="small" />,
            children: [
                {
                    key: 'deploy-section-versions',
                    type: 'section',
                    label: <span className="fs-unmask">Versions</span>,
                },
                ...versionChildren,
                {
                    key: 'deploy-section-query-file',
                    type: 'section',
                    label: <span className="fs-unmask">Query File</span>,
                },
                ...queryFileChildren,
            ],
        };
    }, [deployDropdown, pendingDeployVersion]);

    // Kebab ("⋮") menu per Figma node 3173:7654: Download / Retrain /
    // Duplicate / Move Task / Settings / Delete (Delete is the red
    // destructive entry).
    const kebabMenuItems = useMemo(() => {
        if (isViewer) return [];
        return [
            // Deploy moves in here (as a submenu) when Retrain / Revaluate
            // takes the primary header button.
            ...(deployKebabItem ? [deployKebabItem] : []),
            {
                key: 'download-model',
                label: downloadDisabledReason ? (
                    <TooltipTrigger text={downloadDisabledReason} placement="left">
                        <span className="fs-unmask">Download</span>
                    </TooltipTrigger>
                ) : (
                    <span className="fs-unmask">Download</span>
                ),
                icon: <SparkIcon slug="download" size="small" />,
                disabled: Boolean(downloadDisabledReason),
                onClick: () => handleDownloadModel(),
            },
            // Companion of the Retrain primary button: preview what changed
            // in the training datasets before committing to a retrain.
            ...(mainAction === 'retrain' && changeStatus.latestTrainingEventId
                ? [
                    {
                        key: 'versions-diff',
                        label: <span className="fs-unmask">Versions diff</span>,
                        icon: <SparkIcon slug="diff" size="small" />,
                        suffix: (
                            <TooltipTrigger
                                text={
                                    'See how the training datasets differ between the ' +
                                    'current dataset with the dataset from the last ' +
                                    'completed version.'
                                }
                                placement="left"
                                className="spark-dropdown-item-suffix"
                            >
                                <SparkIcon slug="circle-info" size="small" />
                            </TooltipTrigger>
                        ),
                        onClick: () => setIsVersionsDiffOpen(true),
                    },
                ]
                : []),
            // Always offered — even when Retrain (or the first-run Train,
            // which opens the same dialog) is already the primary header
            // button — so the kebab menu stays consistent.
            {
                key: 'retrain-task',
                label: <span className="fs-unmask">Retrain</span>,
                icon: <SparkIcon slug="brain-icon" size="small" />,
                disableWhenReadOnly: true,
                onClick: handleRetrainClick,
            },
            {
                key: 'duplicate-task',
                label: <span className="fs-unmask">Duplicate</span>,
                icon: <SparkIcon slug="copy" size="small" />,
                disableWhenReadOnly: true,
                onClick: () => handleDuplicateTask(),
            },
            {
                key: 'move-task',
                label: isMoveLocked ? (
                    <TooltipTrigger text={MOVE_LOCKED_TOOLTIP} placement="left">
                        <span className="fs-unmask">Move Task</span>
                    </TooltipTrigger>
                ) : (
                    <span className="fs-unmask">Move Task</span>
                ),
                icon: <SparkIcon slug="arrow-right" size="small" />,
                disableWhenReadOnly: true,
                disabled: isMoveLocked,
                onClick: () => {
                    if (!isMoveLocked) setIsMoveModalOpen(true);
                },
            },
            {
                key: 'task-settings',
                label: <span className="fs-unmask">Settings</span>,
                icon: <SparkIcon slug="settings" size="small" />,
                disableWhenReadOnly: true,
                onClick: () => handleEditTask(firstUsecase, 'umbrella'),
            },
            {
                key: 'delete-task',
                danger: true,
                icon: <SparkIcon slug="trash" size="small" />,
                label: <span className="fs-unmask">Delete</span>,
                disableWhenReadOnly: true,
                onClick: () => {
                    openConfirmMenuDialog({
                        title: `Delete task "${taskTitle}"?`,
                        body: 'All data, prompts, evaluations, and models associated with this task will be permanently deleted. This action can’t be undone.',
                        primaryLabel: 'Delete Task',
                        tertiaryLabel: 'Cancel',
                        onConfirm: handleDeleteTask,
                    });
                },
            },
        ];
    }, [
        isViewer,
        deployKebabItem,
        mainAction,
        changeStatus.latestTrainingEventId,
        downloadDisabledReason,
        handleDownloadModel,
        handleRetrainClick,
        handleDuplicateTask,
        isMoveLocked,
        handleEditTask,
        firstUsecase,
        taskTitle,
        handleDeleteTask,
    ]);

    const handleObjectiveChange = useCallback((usecaseId) => {
        setCurrentUsecaseId(usecaseId);
        updateTaskSearchParams({ task_id: usecaseId });
    }, [setCurrentUsecaseId, updateTaskSearchParams]);

    // ----- Add data / generate-with-AI window events (Data Overview tab) -----

    useEffect(() => {
        const handler = async (event) => {
            const requestId = ++addDataRequestRef.current;
            const source = event?.detail?.source || 'upload';
            const usecaseId = event?.detail?.usecaseId;
            const detailFiles = Array.isArray(event?.detail?.files)
                ? event.detail.files.filter((f) => f instanceof File)
                : [];
            const fallbackFile =
                event?.detail?.file instanceof File ? event.detail.file : null;
            const droppedFiles =
                detailFiles.length > 0 ? detailFiles : fallbackFile ? [fallbackFile] : null;

            if (droppedFiles?.length >= 2) {
                const clientId = allUsecases.find((u) => u._id === usecaseId)?.client_id;
                let openModal = true;
                try {
                    openModal = await gateDroppedFilesForUploadModal(droppedFiles, {
                        clientId,
                        source,
                    });
                } catch (error) {
                    console.error('Error checking dropped file columns:', error);
                }
                if (requestId !== addDataRequestRef.current) return;
                if (!openModal) return;
            }

            if (usecaseId) {
                setCurrentUsecaseId(usecaseId);
                updateTaskSearchParams({ task_id: usecaseId });
            }
            setUploadSource(source);
            setUploadTarget(event?.detail?.uploadTarget);
            setUploadDroppedFiles(droppedFiles);
            setIsUploadModalOpen(true);
        };
        window.addEventListener('task:add-data', handler);
        return () => window.removeEventListener('task:add-data', handler);
    }, [allUsecases, setCurrentUsecaseId, updateTaskSearchParams]);

    useEffect(() => {
        const handler = (event) => {
            const usecaseId = event?.detail?.usecaseId;
            if (usecaseId) {
                setCurrentUsecaseId(usecaseId);
                updateTaskSearchParams({ task_id: usecaseId });
            }
            setIsGenerateWithAiOpen(true);
        };
        window.addEventListener('task:generate-with-ai', handler);
        return () => window.removeEventListener('task:generate-with-ai', handler);
    }, [setCurrentUsecaseId, updateTaskSearchParams]);

    useEffect(() => {
        const handler = (event) => {
            const usecaseId = event?.detail?.usecaseId;
            if (usecaseId) {
                setCurrentUsecaseId(usecaseId);
                updateTaskSearchParams({ task_id: usecaseId });
            }
            setIsRetrievalAgentOpen(true);
        };
        window.addEventListener('task:retrieval-agent', handler);
        return () => window.removeEventListener('task:retrieval-agent', handler);
    }, [setCurrentUsecaseId, updateTaskSearchParams]);

    // ----- Tab flows -----

    /**
     * Legacy `moveToScreen(id, screen, fromEval)` contract used by the mounted
     * screens: step keys become tab switches; everything else (settings,
     * models, ...) falls through to the app-level navigation.
     */
    const wrappedMoveToScreen = useCallback(
        (id, screen, fromEval = false) => {
            const go = () => {
                if (screen === 'playground') {
                    openPlayground();
                    return;
                }
                if (screen && TAB_SCREEN_KEYS.has(screen)) {
                    openTab(screen, id, { fromEvaluation: fromEval });
                    return;
                }
                if (typeof moveToScreen === 'function') {
                    moveToScreen(id, screen, fromEval);
                }
            };
            if (activeTabKey === 'model-setup' && screen !== 'model-setup') {
                void flushModelSetupSaves().then(go);
                return;
            }
            go();
        },
        [moveToScreen, openTab, openPlayground, activeTabKey, flushModelSetupSaves]
    );

    const showInferenceTab = Boolean(isSaas && effectiveUsecase?.is_deployed);

    const tabItems = useMemo(
        () =>
            TASK_PAGE_TABS
                .filter((tab) => tab.key !== 'inference' || showInferenceTab)
                .map((tab) => ({
                    id: tab.key,
                    label: tab.label,
                })),
        [showInferenceTab]
    );

    useEffect(() => {
        if (!usecases.length || isFetchingUsecases) return;
        if (activeTabKey === 'inference' && !showInferenceTab) {
            openTab('data-overview', activeUsecaseId);
        }
    }, [activeTabKey, showInferenceTab, openTab, activeUsecaseId, usecases.length, isFetchingUsecases]);

    const isValidTask = usecases.length > 0;

    if (!isValidTask) {
        // Still loading — show spinner instead of blank page on refresh.
        if (!allApps.length || isFetchingUsecases) {
            return (
                <div className="task-page-loading" role="status" aria-label="Loading task">
                    <div className="task-page-loading__spinner" aria-hidden="true" />
                </div>
            );
        }
        // Data fully loaded, task genuinely not found — redirect synchronously.
        return <Navigate to="/" replace />;
    }

    return (
        <div className="task-page">
            <ProjectCreatedBar
                visible={projectCreatedBar.visible}
                projectName={projectCreatedBar.projectName}
                taskName={projectCreatedBar.taskName}
                onClose={closeProjectCreatedBar}
                onStartTaskSteps={handleStartDuplicatedTask}
                isProjectCreation={false}
                actionButtonLabel="Go to Task"
            />

            <TaskPageHeader
                title={taskTitle}
                usecases={usecases}
                activeUsecaseId={activeUsecaseId}
                onObjectiveChange={handleObjectiveChange}
                onCreateProject={onCreateProject}
                onAddTask={onAddTask}
                isViewer={isViewer}
                onOpenPlayground={openPlayground}
                kebabMenuItems={kebabMenuItems}
                showDeploy={showDeploy}
                deployMenuItems={deployMenuItems}
                deployDropdown={deployDropdown}
                mainAction={mainAction}
                onRetrainClick={handleRetrainClick}
                onTrainClick={handleRetrainClick}
                onReevaluateClick={handleReevaluateClick}
                deploymentTag={deploymentTag}
                onDeployMenuOpenChange={onMenuOpenChange}
                undeployLabel={undeployLabel}
                onUndeploy={handleUndeploy}
                undeployMenuItems={undeployMenuItems}
                guidedTourId={taskTourAvailable ? TASK_MANAGEMENT_WALKTHROUGH_ID : null}
            />

            <div className="task-page-tabs fs-unmask">
                <Tabs
                    items={tabItems}
                    activeId={activeTabKey}
                    onChange={(tabKey) => {
                        if (activeTabKey === 'model-setup' && tabKey !== 'model-setup') {
                            void flushModelSetupSaves().then(() => openTab(tabKey, activeUsecaseId));
                            return;
                        }
                        openTab(tabKey, activeUsecaseId);
                    }}
                    size="medium"
                />
            </div>

            <div className="task-page-tab-content">
                {activeTabKey === 'data-overview' && (
                    <div className="task-page-tab-body task-page-tab-body--padded">
                        <DataOverviewCard
                            subGroup={subGroup}
                            activeUsecaseId={activeUsecaseId}
                            onActiveUsecaseChange={handleObjectiveChange}
                            dataOverviewRefreshKey={dataOverviewRefreshKey}
                        />
                    </div>
                )}
                {activeTabKey === 'model-setup' && (
                    <div className="task-page-tab-body">
                        <ModelSetup
                            key={activeUsecaseId || 'model-setup'}
                            ref={modelSetupRef}
                            embeddedInTaskDrawer
                        />
                    </div>
                )}
                {activeTabKey === 'data-selection' && (
                    <div className="task-page-tab-body">
                        <DataSelection
                            embeddedInTaskDrawer
                            moveToScreen={wrappedMoveToScreen}
                            onDataOverviewRefresh={refreshDataOverview}
                            onRetrainRequest={() => {
                                void flushModelSetupSaves().then(() => setIsRetrainModalOpen(true));
                            }}
                        />
                    </div>
                )}
                {activeTabKey === 'evaluation-from-session' && (
                    <div
                        className="task-page-tab-body task-page-tab-body--padded"
                        data-walkthrough="evaluation-panel"
                    >
                        <Evaluation
                            moveToScreen={wrappedMoveToScreen}
                            hideTaskOverview
                            onRetrainClick={handleRetrainClick}
                        />
                    </div>
                )}
                {activeTabKey === 'model-details' && (
                    <div className="task-page-tab-body task-page-tab-body--padded">
                        <TaskModelDetailsTab activeUsecase={effectiveUsecase} />
                    </div>
                )}
                {activeTabKey === 'inference' && (
                    <div className="task-page-tab-body task-page-tab-body--padded">
                        <TaskInferenceTab activeUsecase={effectiveUsecase} />
                    </div>
                )}
            </div>

            {/* Playground — full-screen workspace below the main navigation
                (new Figma flow replaces the old right-side drawer). */}
            {isPlaygroundOpen && (
                <div className="pg-overlay" role="dialog" aria-label="Playground">
                    <Playground onExit={closePlayground} />
                </div>
            )}

            {/* Task-management tour (Figma 9607-20688): auto-starts once per
                user on the first task page with evaluation results, is held
                while the Playground or another tab is up, and ends for good
                when the page is left — Esc / X dismiss it, "Got it" completes
                it, the header button replays it. */}
            {taskTourAvailable && (
                <WalkthroughRunner
                    walkthroughId={TASK_MANAGEMENT_WALKTHROUGH_ID}
                    currentPage={taskTourPage}
                    pageOrder={TASK_MANAGEMENT_PAGE_ORDER}
                    autoStart={WALKTHROUGH_AUTO_START.neverSeen}
                    completeOnLeave
                />
            )}

            <ModelProcessingModal
                open={isProcessingModalOpen}
                onClose={closeProcessingModal}
                onNotifyMe={canNotifyByEmail ? handleNotifyMe : undefined}
            />

            {singleDeployUsecase && (
                <QueryFileModal
                    open={queryFileModal.open}
                    language={queryFileModal.language}
                    onClose={() =>
                        setQueryFileModal((prev) => ({ ...prev, open: false }))
                    }
                    clientId={singleDeployUsecase.client_id}
                    usecaseId={singleDeployUsecase._id}
                    taskName={singleDeployUsecase.usecase_name || ''}
                    projectName={queryFileProjectName}
                    taskType={singleDeployUsecase.task_type}
                />
            )}

            <AddDataModal
                open={isUploadModalOpen}
                onClose={() => {
                    setIsUploadModalOpen(false);
                    setUploadTarget(undefined);
                    setUploadDroppedFiles(null);
                }}
                source={uploadSource}
                uploadTarget={uploadTarget}
                initialDroppedFiles={uploadDroppedFiles}
                onInitialDroppedFilesConsumed={() => setUploadDroppedFiles(null)}
                usecase={effectiveUsecase}
                subGroup={subGroup}
                onSuccess={() => refreshUsecases?.()}
            />

            <GenerateDataWithAIModal
                visible={isGenerateWithAiOpen}
                onCancel={() => setIsGenerateWithAiOpen(false)}
                usecase={effectiveUsecase}
                onGenerate={(payload) =>
                    runAIGenerationDataset({ usecase: effectiveUsecase, payload, seedGenerationProgress }).then((res) => {
                        setIsGenerateWithAiOpen(false);
                        refreshUsecases?.();
                        return res;
                    })
                }
            />

            <RetrievalAgentModal
                visible={isRetrievalAgentOpen}
                onCancel={() => setIsRetrievalAgentOpen(false)}
                usecase={effectiveUsecase}
                onInitiate={(payload) =>
                    runRetrievalAgent({ usecase: effectiveUsecase, payload, seedRetrievalAgentProgress }).then((res) => {
                        setIsRetrievalAgentOpen(false);
                        refreshUsecases?.();
                        return res;
                    })
                }
            />

            <DuplicateTaskModal
                isOpen={isDuplicateTaskModalOpen}
                onClose={closeDuplicateTaskModal}
                originalTask={taskToDuplicate}
                allProjects={allApps || []}
                onDuplicateTask={handleCreateDuplicateTask}
            />

            <EditTaskModal
                isOpen={isEditTaskModalOpen}
                onClose={closeEditTaskModal}
                projectName={taskToEdit?.group_name || ''}
                allProjects={allApps || []}
                task={taskToEdit}
                mode={editTaskMode}
                onUpdateTask={handleUpdateTask}
            />

            <MoveTasksModal
                open={isMoveModalOpen}
                itemCount={usecases.length}
                itemNames={[taskTitle].filter(Boolean)}
                destinations={moveDestinations}
                onClose={() => setIsMoveModalOpen(false)}
                onMove={handleMoveTask}
                loading={moveLoading}
            />

            <RetrainTaskModal
                open={isRetrainModalOpen}
                title={mainAction === 'train' ? 'Train' : 'Retrain'}
                onClose={() => setIsRetrainModalOpen(false)}
                moveToScreen={wrappedMoveToScreen}
                onDataOverviewRefresh={refreshDataOverview}
                onTrainingStarted={() => setIsProcessingModalOpen(true)}
            />

            {/* Confirm modal for the Revaluate primary button — same view-only
                params summary + recalculate-evaluation flow as the
                Evaluations tab. */}
            <RecalculateEvaluationParamsModal
                open={isReevaluateModalOpen}
                usecase={effectiveUsecase}
                changeStatus={changeStatus}
                confirming={isReevaluating}
                onCancel={() => setIsReevaluateModalOpen(false)}
                onConfirm={handleConfirmReevaluate}
            />

            {/* Kebab "Versions diff" (shown alongside the Retrain primary
                button): the existing Versions Diff modal comparing the last
                completed training version's train snapshot against the
                CURRENT live datasets. */}
            {isVersionsDiffOpen && singleUsecase && changeStatus.latestTrainingEventId ? (
                <VersionsDiffModal
                    clientId={singleUsecase.client_id}
                    usecaseId={singleUsecase._id}
                    fromEventId={changeStatus.latestTrainingEventId}
                    toEventId={CURRENT_VERSION_EVENT_ID}
                    fromVersionLabel={
                        changeStatus.latestTrainingVersion != null
                            ? `V${changeStatus.latestTrainingVersion}`
                            : 'Last version'
                    }
                    toVersionLabel="Current"
                    currentUsecase={singleUsecase}
                    taskName={taskTitle}
                    taskType={singleUsecase.task_type}
                    singleLabel={
                        singleUsecase.task_type === TaskType.classification &&
                        !singleUsecase.is_multilabel
                    }
                    allLabels={Object.keys(
                        singleUsecase.entity_names_count || singleUsecase.label_names_count || {}
                    )}
                    onClose={() => setIsVersionsDiffOpen(false)}
                />
            ) : null}

            <ConfigureDeploymentFlowModals
                configureFlow={configureFlow}
                allUsecases={allUsecases}
            />

            {trainingBanner}
        </div>
    );
};

export default TaskPage;
